import { useEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';

import { useBackgroundActivityRegistry } from '@hooks/useBackgroundActivityRegistry';
import { ToastType } from '@models';
import {
  BackgroundActivity,
  BackgroundActivityKind,
  BackgroundActivityStatus
} from '@models/backgroundActivities';
import { useBackgroundActivityStore } from '@services/backgroundActivityStore';
import { useStore } from '@store';

function BackgroundActivityNavigationGuard(): null {
  const registry = useBackgroundActivityRegistry();
  const activities = useBackgroundActivityStore((state) => state.activities);
  const { setToast } = useStore((state) => ({ setToast: state.setToast }), shallow);
  const toastedActivityIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!registry.hasInFlight()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [registry]);

  useEffect(() => {
    const currentActivityIds = new Set(Object.keys(activities));
    for (const id of toastedActivityIds.current) {
      if (!currentActivityIds.has(id)) {
        toastedActivityIds.current.delete(id);
      }
    }

    for (const activity of Object.values(activities)) {
      if (toastedActivityIds.current.has(activity.id)) continue;
      if (activity.restored) {
        toastedActivityIds.current.add(activity.id);
        continue;
      }
      if (activity.status === BackgroundActivityStatus.COMPLETED) {
        toastedActivityIds.current.add(activity.id);
        setToast({
          message: getSuccessMessageKey(activity.kind),
          type: ToastType.SUCCESS
        });
      } else if (activity.status === BackgroundActivityStatus.FAILED) {
        toastedActivityIds.current.add(activity.id);
        setToast({
          message: activity.error ?? 'errors.somethingWentWrong',
          type: ToastType.ERROR
        });
      }
    }
  }, [activities, setToast]);

  return null;
}

function getSuccessMessageKey(kind: BackgroundActivity['kind']): string {
  switch (kind) {
    case BackgroundActivityKind.UPLOAD:
      return 'backgroundActivity.toast.uploadSuccess';
  }
}

export default BackgroundActivityNavigationGuard;
